// src/utils/addressFormat.js
// Shared, country-aware address + distance formatting. CommonJS so it can be
// used by both the React app (import) and the Express server (require), the
// same way signupUrlDetector.js is shared.
//
// Storage model (see db/ensureSchema.js): admin-1 → b_state, admin-2 → b_city,
// admin-3 → b_subdistrict, postal → b_zip_code, street → b_address. The flat
// columns hold romanized (English) display values; b_address_components (JSONB)
// holds the bilingual + coded record so we can render Thai script on demand.

const MILES_PER_KM = 1.60934;

// Countries that use miles for road distance. Everyone else gets km. Our live
// audience is US + Thailand; US is the only imperial one that matters here.
const IMPERIAL_COUNTRIES = new Set(['united states', 'usa', 'us']);

function isMetricCountry(country) {
  return !IMPERIAL_COUNTRIES.has(String(country || '').trim().toLowerCase());
}

function isThai(b) {
  const c = b && b.b_address_components;
  if (c && c.countryCode === 'TH') return true;
  return String((b && b.b_country) || '').trim() === 'Thailand';
}

// Pull a level's display name in the requested language, falling back to the
// flat column (which is romanized) when the structured blob is absent.
function levelName(components, level, lang, flatFallback) {
  const node = components && components[level];
  if (node) return (lang === 'th' && node.nameTh) ? node.nameTh : node.nameEn;
  return flatFallback || '';
}

/**
 * Format a business address as a single line, country-aware.
 * @param {object} b - business row (b_address, b_city, b_state, b_subdistrict,
 *                      b_zip_code, b_country, b_address_components)
 * @param {object} [opts]
 * @param {'en'|'th'} [opts.lang='en'] - script for Thai addresses
 * @param {boolean}   [opts.includeStreet=true] - include the street line
 * @returns {string}
 */
function formatBusinessAddress(b, opts = {}) {
  if (!b) return '';
  const lang = opts.lang || 'en';
  const includeStreet = opts.includeStreet !== false;
  const c = b.b_address_components || null;
  const street = String(b.b_address || '').trim();
  const zip = String(b.b_zip_code || (c && c.postalCode) || '').trim();

  if (isThai(b)) {
    // Thai order is small → large: street, sub-district, district, province, postal.
    const sub  = levelName(c, 'subdistrict', lang, b.b_subdistrict);
    const dist = levelName(c, 'district', lang, b.b_city);
    const prov = levelName(c, 'province', lang, b.b_state);
    const parts = [includeStreet ? street : '', sub, dist, prov]
      .map((s) => String(s || '').trim()).filter(Boolean);
    let line = parts.join(', ');
    if (zip) line += (line ? ' ' : '') + zip;
    return line;
  }

  // US + generic international: street, "city, region zip"[, country].
  const city = String(b.b_city || '').trim();
  const region = String(b.b_state || '').trim();
  const cityRegion = [city, region].filter(Boolean).join(', ');
  const parts = [includeStreet ? street : '', cityRegion].map((s) => s.trim()).filter(Boolean);
  let line = parts.join(', ');
  if (zip) line += (line ? ' ' : '') + zip;
  const country = String(b.b_country || '').trim();
  if (country && isMetricCountry(country)) line += (line ? ', ' : '') + country; // append country for non-US
  return line;
}

/**
 * Format a service radius for display. Storage is ALWAYS miles (matching math
 * depends on it); this only changes what the user sees — km for metric
 * countries, miles for the US.
 * @param {number|string} miles - stored value (miles)
 * @param {string} country - the business's country
 * @returns {string} e.g. "30 miles" or "48 km"
 */
function formatServiceDistance(miles, country) {
  const n = Number(miles);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (isMetricCountry(country)) {
    return `${Math.round(n * MILES_PER_KM)} km`;
  }
  return `${n} ${n === 1 ? 'mile' : 'miles'}`;
}

// Convert a user-entered distance (in their display unit) to stored miles, and
// a stored mile value back to the display unit. Used by the radius input.
function toStoredMiles(displayValue, country) {
  const n = Number(displayValue);
  if (!Number.isFinite(n)) return NaN;
  return isMetricCountry(country) ? n / MILES_PER_KM : n;
}
function toDisplayDistance(storedMiles, country) {
  const n = Number(storedMiles);
  if (!Number.isFinite(n)) return '';
  return isMetricCountry(country) ? Math.round(n * MILES_PER_KM) : n;
}

module.exports = {
  isMetricCountry,
  formatBusinessAddress,
  formatServiceDistance,
  toStoredMiles,
  toDisplayDistance,
  MILES_PER_KM,
};
